import React from 'react';

import MyPiggyIcon from '../UI/MyPiggyIcon';


const formatDate = (value) => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};


const daysUntil = (value) => {
  if (!value) return null;

  const end = new Date(value).getTime();

  if (Number.isNaN(end)) {
    return null;
  }

  return Math.max(
    0,
    Math.ceil(
      (end - Date.now()) /
        (24 * 60 * 60 * 1000)
    )
  );
};


const SubscriptionStatusBanner = ({
  audience = 'parent',
  effectiveStatus,
  trialEndsAt,
  graceEndsAt,
  ownerName = '',
  canManageBilling = false,
  className = '',
}) => {
  const isKid =
    audience === 'kid';

  const organizerReference =
    ownerName
      ? `${ownerName}, your Family Organizer`
      : 'your Family Organizer';

  if (effectiveStatus === 'trial') {
    const daysRemaining =
      daysUntil(trialEndsAt);

    // Keep the normal 30-day trial quiet until the reminder window.
    if (
      daysRemaining === null ||
      daysRemaining > 10
    ) {
      return null;
    }

    if (isKid) {
      return null;
    }

    return (
      <div
        role="status"
        aria-live="polite"
        className={`${className} rounded-2xl border-2 border-blue-200 bg-blue-50 px-4 py-4 shadow-sm dark:border-blue-800 dark:bg-blue-950/40`}
      >
        <div className="flex items-start gap-3">
          <MyPiggyIcon name="locked-read-only" size="md" />

          <div>
            <p className="font-extrabold text-blue-900 dark:text-blue-100">
              Your MyPiggy trial has {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining.
            </p>

            <p className="mt-1 text-sm leading-relaxed text-blue-800 dark:text-blue-200">
              Your family still has full access. The trial ends {formatDate(trialEndsAt)}.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (effectiveStatus === 'grace') {
    const graceDate =
      formatDate(graceEndsAt);

    return (
      <div
        role="status"
        aria-live="polite"
        className={`${className} rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-4 shadow-sm dark:border-amber-700 dark:bg-amber-950/40`}
      >
        <div className="flex items-start gap-3">
          <MyPiggyIcon name="locked-read-only" size="md" />

          <div>
            <p className="font-extrabold text-amber-950 dark:text-amber-100">
              {isKid
                ? 'Your MyPiggy still has full access for now.'
                : 'Your family is in a payment grace period.'}
            </p>

            <p className="mt-1 text-sm leading-relaxed text-amber-900 dark:text-amber-200">
              {isKid
                ? `You can keep using MyPiggy normally. Please ask ${organizerReference} to check their MyPiggy message or email and make sure your MyPiggy subscription stays active.`
                : canManageBilling
                  ? `MyPiggy is still fully available${graceDate ? ` through ${graceDate}` : ''}. Update billing before the grace period ends to avoid read-only access.`
                  : `MyPiggy is still fully available${graceDate ? ` through ${graceDate}` : ''}. Please contact ${organizerReference} to update billing before the grace period ends.`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (effectiveStatus === 'expired') {
    return (
      <div
        role="status"
        aria-live="polite"
        className={`${className} rounded-2xl border-2 border-red-300 bg-red-50 px-4 py-4 shadow-sm dark:border-red-800 dark:bg-red-950/40`}
      >
        <div className="flex items-start gap-3">
          <MyPiggyIcon name="locked-read-only" size="md" />

          <div>
            <p className="font-extrabold text-red-900 dark:text-red-100">
              {isKid
                ? 'Your MyPiggy is read-only right now.'
                : 'This family is currently read-only.'}
            </p>

            <p className="mt-1 text-sm leading-relaxed text-red-800 dark:text-red-200">
              {isKid
                ? `You can still look at your money, savings, chores, and reports, but you can’t make changes. Please ask ${organizerReference} to check their MyPiggy message or email to restore full access.`
                : 'You can still view family information and history, but changes are unavailable until the subscription becomes active again.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};


export default SubscriptionStatusBanner;
